import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Download, Upload, Pencil, Trash2, QrCode, PauseCircle, GitMerge, MoreHorizontal,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Modal from "../components/Modal";
import WarehouseTabs from "../components/WarehouseTabs";
import HoldModal from "../components/HoldModal";
import ImportDialog from "../components/ImportDialog";
import BulkEditDialog from "../components/BulkEditDialog";
import CompatibleModelsDialog from "../components/CompatibleModelsDialog";
import { Button } from "../components/ui/button";
import { FormError } from "../components/ui/feedback";
import { FlowpiAlertDialog, FlowpiDialog } from "../components/ui/flowpi-dialog";
import { Field as FormField, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  DataValue,
  PassiveBadge,
  PrimarySecondaryValue,
} from "../components/ui/data-presentation";
import {
  ListControls,
  ListErrorNotice,
  ListPagination,
  ListTable,
  ListTableRefreshingRow,
  ListTableShell,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnPriceRangeFilter,
  ColumnTextFilter,
  GlobalSearch,
  matchesNumericRange,
  shouldShowClearAll,
  useSearchFilterState,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { formatIDR } from "../lib/format";
import {
  buildItemDeleteConfig,
  buildItemEditPayload,
  normalizeItemDraft,
  replaceItemSnapshot,
} from "../lib/itemCrudST5";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";

const emptyItem = {
  part_number: "",
  model: "",
  compatible_models: [],
  part_name: "",
  description: "",
  price: 0,
  rack_location: "",
  low_stock_threshold: 5,
  stock: {},
};

const INVENTORY_TABLE_GEOMETRY = Object.freeze([
  { key: "part-number", kind: "fixed", width: 150 },
  { key: "model", kind: "fixed", width: 190 },
  { key: "name", kind: "fill", minWidth: 300 },
  { key: "rack", kind: "fixed", width: 120 },
  { key: "stock", kind: "fixed", width: 100 },
  { key: "price", kind: "fixed", width: 130 },
  { key: "actions", kind: "utility", width: 104 },
]);

const EMPTY_RANGE = Object.freeze({ min: "", max: "" });

export default function Inventory() {
  const { user, warehouses } = useAuth();
  const isAdmin = user?.role === "admin";

  const [activeWh, setActiveWh] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");

  // Column filters
  const [fPart, setFPart] = useState("");
  const [fModel, setFModel] = useState("");
  const [fName, setFName] = useState("");
  const [fRack, setFRack] = useState("");
  const [fStockMin, setFStockMin] = useState("");
  const [fStockMax, setFStockMax] = useState("");
  const [fPriceMin, setFPriceMin] = useState("");
  const [fPriceMax, setFPriceMax] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const searchFilters = useSearchFilterState({ onPageReset: () => setPage(1) });

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [itemSaveError, setItemSaveError] = useState("");
  const [itemSaving, setItemSaving] = useState(false);
  const itemSaveInFlightRef = useRef(false);
  const [tagInput, setTagInput] = useState("");
  const [qrItem, setQrItem] = useState(null);
  const itemBaselineRef = useRef(null);

  const [importOpen, setImportOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [compatModelsOpen, setCompatModelsOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdItem, setHoldItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Set default active tab = MAIN
  useEffect(() => {
    if (!activeWh && warehouses.length > 0) {
      setActiveWh(warehouses[0].id);
    }
  }, [warehouses, activeWh]);

  const load = async () => {
    setLoading(true);
    setListError("");
    try {
      const { data } = await api.get("/items", { params: { q: "" } });
      setItems(data);
    } catch (e) {
      setListError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-warehouse counts (for tab badges)
  const tabCounts = useMemo(() => {
    const c = {};
    warehouses.forEach((w) => {
      c[w.id] = items.filter((it) => (it.stock?.[w.id] ?? 0) > 0).length;
    });
    return c;
  }, [items, warehouses]);

  const appliedColumnFilters = searchFilters.state.columnFilters;
  const appliedGlobalSearch = searchFilters.state.globalSearch.trim().toLowerCase();
  const stockRange = appliedColumnFilters.stock || EMPTY_RANGE;
  const priceRange = appliedColumnFilters.price || EMPTY_RANGE;

  // Filtered by active warehouse + all applied Search/Filter constraints (logical AND)
  const filtered = useMemo(() => {
    if (!activeWh) return [];
    return items.filter((it) => {
      const stock = it.stock?.[activeWh] ?? 0;
      if (appliedGlobalSearch) {
        const searchable = [
          it.part_number,
          it.model,
          ...(it.compatible_models || []),
          it.part_name,
          it.rack_location,
        ].map((value) => String(value || "").toLowerCase());
        if (!searchable.some((value) => value.includes(appliedGlobalSearch))) return false;
      }
      if (appliedColumnFilters.part && !String(it.part_number || "").toLowerCase().includes(appliedColumnFilters.part.toLowerCase())) return false;
      if (appliedColumnFilters.model) {
        const modelQuery = appliedColumnFilters.model.toLowerCase();
        const matchesModel = (it.model || "").toLowerCase().includes(modelQuery);
        const matchesCompat = (it.compatible_models || []).some((m) => m.toLowerCase().includes(modelQuery));
        if (!matchesModel && !matchesCompat) return false;
      }
      if (appliedColumnFilters.name && !(it.part_name || "").toLowerCase().includes(appliedColumnFilters.name.toLowerCase())) return false;
      if (appliedColumnFilters.rack && !(it.rack_location || "").toLowerCase().includes(appliedColumnFilters.rack.toLowerCase())) return false;
      if ((stockRange.min !== "" || stockRange.max !== "") && !matchesNumericRange(stock, stockRange)) return false;
      const price = Number(it.price || 0);
      if ((priceRange.min !== "" || priceRange.max !== "") && !matchesNumericRange(price, priceRange)) return false;
      return true;
    });
  }, [items, activeWh, appliedGlobalSearch, appliedColumnFilters, stockRange, priceRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => setPage(1), [activeWh]);

  const clearAllFilters = () => {
    setQ("");
    setFPart(""); setFModel(""); setFName(""); setFRack("");
    setFStockMin(""); setFStockMax("");
    setFPriceMin(""); setFPriceMax("");
    setFilterResetKey((current) => current + 1);
    searchFilters.clearAll();
  };

  const activeSearchFilterState = searchFilters.state;
  const hasAppliedColumnFilters = Object.values(appliedColumnFilters).some((value) => (
    value && typeof value === "object"
      ? Object.values(value).some((part) => part !== "" && part != null)
      : value !== "" && value != null
  ));
  const hasAppliedFilters = Boolean(appliedGlobalSearch || hasAppliedColumnFilters);

  const openCreate = () => {
    const stock = Object.fromEntries(warehouses.map((w) => [w.id, 0]));
    const next = { ...emptyItem, stock };
    itemBaselineRef.current = normalizeItemDraft(next, warehouses.map((w) => w.id));
    setEditing(next);
    setTagInput("");
    setItemSaveError("");
    setEditOpen(true);
  };
  const openEdit = (item) => {
    const next = { ...item, stock: { ...(item.stock || {}) } };
    itemBaselineRef.current = normalizeItemDraft(next, warehouses.map((w) => w.id));
    setEditing(next);
    setTagInput("");
    setItemSaveError("");
    setEditOpen(true);
  };

  const itemDirty = Boolean(editing && itemBaselineRef.current && JSON.stringify(normalizeItemDraft(editing, warehouses.map((w) => w.id))) !== JSON.stringify(itemBaselineRef.current));
  const itemGuard = useUnsavedChangesGuard(itemDirty && !itemSaving);
  const closeItemEditor = () => itemGuard.requestLeave(() => setEditOpen(false));

  const save = async () => {
    if (itemSaveInFlightRef.current) return;
    itemSaveInFlightRef.current = true;
    setItemSaving(true);
    setItemSaveError("");
    try {
      if (editing.id) {
        // Update base fields
        const editResponse = await api.put(
          `/items/${editing.id}`,
          buildItemEditPayload(editing)
        );
        let savedItem = editResponse.data;
        // If stock was edited, apply stock changes via dedicated endpoint
        const originalStock = items.find((i) => i.id === editing.id)?.stock || {};
        const newStock = editing.stock || {};
        const changes = {};
        for (const w of warehouses) {
          const before = Number(originalStock[w.id] || 0);
          const after = Number(newStock[w.id] || 0);
          if (after !== before) changes[w.id] = after;
        }
        if (Object.keys(changes).length > 0) {
          const stockResponse = await api.put(`/items/${editing.id}/stock`, { stock: changes });
          savedItem = stockResponse.data;
        }
        setEditing(savedItem);
        setItems((current) => replaceItemSnapshot(current, savedItem));
        toast.success("Item updated");
      } else {
        await api.post(`/items`, {
          ...editing,
          price: Number(editing.price),
          low_stock_threshold: Number(editing.low_stock_threshold),
          stock: Object.fromEntries(
            Object.entries(editing.stock || {}).map(([k, v]) => [k, Number(v) || 0])
          ),
        });
        toast.success("Item created");
      }
      setEditOpen(false);
      itemBaselineRef.current = null;
      load();
    } catch (e) {
      setItemSaveError(formatApiError(e));
    } finally {
      itemSaveInFlightRef.current = false;
      setItemSaving(false);
    }
  };

  const remove = (item) => {
    setDeleteError("");
    setDeleteTarget(item);
  };

  const confirmRemove = async () => {
    if (!deleteTarget || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError("");
    try {
      await api.delete(`/items/${deleteTarget.id}`, buildItemDeleteConfig(deleteTarget));
      toast.success("Item deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      setDeleteError(formatApiError(e));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const exportCsv = async () => {
    const res = await api.get("/items/export/xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "items.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const openHold = (item) => {
    setHoldItem(item);
    setHoldOpen(true);
  };

  const activeWhObj = warehouses.find((w) => w.id === activeWh);

  return (
    <OrdinaryPageBody data-testid="inventory-page">
      <OrdinaryPageHeader
        title="Inventory"
        actions={<>
          <RefreshButton
            onClick={load}
            loading={loading}
            data-action-category="retry-refresh"
            data-testid="inventory-refresh-btn"
            accessibleLabel="Refresh inventory"
          />
          <Button type="button" onClick={exportCsv} variant="export" data-action-category="export-download" data-testid="inventory-export-btn">
            <Download strokeWidth={2} /> Export
          </Button>
          {isAdmin && (
            <>
              <Button type="button" onClick={() => setImportOpen(true)} variant="import" data-action-category="import-upload" data-testid="inventory-import-btn">
                <Upload strokeWidth={2} /> Import Excel
              </Button>
              <Button type="button" onClick={() => setBulkEditOpen(true)} variant="import" data-action-category="import-upload" data-testid="inventory-bulk-edit-btn">
                <Pencil strokeWidth={2} /> Bulk Edit
              </Button>
              <Button type="button" onClick={() => setCompatModelsOpen(true)} variant="info" data-action-category="view-info" data-testid="inventory-compat-models-btn">
                <GitMerge strokeWidth={2} /> Compatible Models
              </Button>
              <Button type="button" onClick={openCreate} variant="default" data-action-category="create-add" data-testid="inventory-new-btn">
                <Plus strokeWidth={2} /> New Item
              </Button>
            </>
          )}
        </>}
        data-testid="inventory-header-row"
      />

      {/* Warehouse Tabs */}
      <ListTableShell
        data-testid="inventory-data-panel"
      >
        <ListControls className="gap-0 xl:flex-row xl:flex-nowrap xl:items-end" data-testid="inventory-branch-search-row">
          <div className="min-w-0 flex-1">
            <WarehouseTabs
              warehouses={warehouses}
              active={activeWh}
              onChange={setActiveWh}
              counts={tabCounts}
              testidPrefix="inv-tab"
            />
          </div>
          <div className="flex min-w-0 justify-end border-b border-zinc-200 px-5 pb-2 pt-4 xl:ml-auto xl:w-[380px] xl:shrink-0">
            <GlobalSearch
              value={q}
              onValueChange={setQ}
              onApply={searchFilters.applyGlobalSearch}
              placeholder="Global Search"
              inputProps={{ "data-testid": "inventory-search-input" }}
            />
          </div>
        </ListControls>

        {listError && items.length > 0 ? (
          <ListErrorNotice onRetry={load} testId="inventory-list-error">{listError}</ListErrorNotice>
        ) : null}

        {/* Table */}
        <ListTableViewport data-testid="inventory-table-wrapper">
          <ListTable geometry={INVENTORY_TABLE_GEOMETRY} data-testid="inventory-table">
            <thead>
              <tr>
                <th>Part #</th>
                <th>Model</th>
                <th>Name</th>
                <th>Rack</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Price</th>
                <th>Actions</th>
              </tr>
              {/* Filter row */}
              <ColumnFilterRow className="bg-white">
                <ColumnFilterCell>
                  <ColumnTextFilter value={fPart} onValueChange={setFPart} onApply={(value) => searchFilters.applyColumnFilter("part", value)} placeholder="Filter…" aria-label="Filter Part #" data-testid="filter-part-number" />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter value={fModel} onValueChange={setFModel} onApply={(value) => searchFilters.applyColumnFilter("model", value)} placeholder="Filter…" aria-label="Filter Model or Compatible Models" data-testid="filter-model" />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter value={fName} onValueChange={setFName} onApply={(value) => searchFilters.applyColumnFilter("name", value)} placeholder="Filter…" aria-label="Filter Name" data-testid="filter-name" />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnTextFilter value={fRack} onValueChange={setFRack} onApply={(value) => searchFilters.applyColumnFilter("rack", value)} placeholder="Filter…" aria-label="Filter Rack" data-testid="filter-rack" />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnPriceRangeFilter
                    key={`stock-${filterResetKey}`}
                    value={{ min: fStockMin, max: fStockMax }}
                    onValueChange={({ min, max }) => { setFStockMin(min); setFStockMax(max); }}
                    onApply={(value) => searchFilters.applyColumnFilter("stock", value)}
                    allowDecimal={false}
                    aria-label="Stock range"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell>
                  <ColumnPriceRangeFilter
                    key={`price-${filterResetKey}`}
                    value={{ min: fPriceMin, max: fPriceMax }}
                    onValueChange={({ min, max }) => { setFPriceMin(min); setFPriceMax(max); }}
                    onApply={(value) => searchFilters.applyColumnFilter("price", value)}
                    precision={2}
                    aria-label="Price range"
                  />
                </ColumnFilterCell>
                <ColumnFilterCell className="align-middle" aria-label="Actions filter controls">
                  <div className="flex h-7 w-full items-center justify-end">
                    {shouldShowClearAll(activeSearchFilterState) || hasAppliedColumnFilters ? (
                      <ClearAllFilters onClick={clearAllFilters} data-testid="filter-clear-all" />
                    ) : null}
                  </div>
                </ColumnFilterCell>
              </ColumnFilterRow>
            </thead>
            <tbody data-testid="inventory-tbody">
              <ListTableRefreshingRow
                active={loading && items.length > 0}
                colSpan={7}
                label="Refreshing inventory..."
                testId="inventory-refreshing"
              />
              {loading && items.length === 0 ? (
                <ListTableState colSpan={7} kind="loading">Loading...</ListTableState>
              ) : listError && items.length === 0 ? (
                <ListTableState colSpan={7} kind="error" onRetry={load}>{listError}</ListTableState>
              ) : pageItems.length === 0 ? (
                <ListTableState
                  colSpan={7}
                  kind={hasAppliedFilters ? "filtered-empty" : "empty"}
                >
                  {hasAppliedFilters
                    ? "No items match the current filters."
                    : `No items found in ${activeWhObj?.name || "this warehouse"}.`}
                </ListTableState>
              ) : (
                pageItems.map((it) => {
                  const stock = it.stock?.[activeWh] ?? 0;
                  const low = stock <= (it.low_stock_threshold || 0);
                  const rowActionCount = isAdmin ? 4 : 2;
                  const usesMoreActions = rowActionCount >= 3;
                  return (
                    <tr key={it.id} data-testid={`inventory-row-${it.part_number}`}>
                      <td className="overflow-hidden text-xs font-semibold tracking-[0.02em] text-zinc-800"><DataValue value={it.part_number} containment="truncate" /></td>
                      <td className="overflow-hidden">
                        <DataValue value={it.model} containment="truncate" className="block font-medium text-zinc-900" />
                        {(it.compatible_models || []).filter(m => m !== it.model).length > 0 && (
                          <div className="break-words text-[11px] text-zinc-400">
                            {it.compatible_models.filter(m => m !== it.model).join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="text-sm">
                        <PrimarySecondaryValue
                          primary={it.part_name}
                          secondary={it.description}
                          className="max-w-md"
                          primaryClassName="font-medium text-zinc-900"
                          secondaryClassName="text-[11px] text-zinc-500"
                          secondaryContainment="truncate"
                        />
                      </td>
                      <td className="overflow-hidden text-xs"><DataValue value={it.rack_location} containment="truncate" /></td>
                      <td className="num">
                        <PassiveBadge tone={stock === 0 ? "danger" : low ? "warning" : "success"} data-testid={`inventory-quantity-${it.part_number}`}>
                          {stock}
                        </PassiveBadge>
                      </td>
                      <td className="num overflow-hidden text-ellipsis whitespace-nowrap text-xs">{formatIDR(it.price)}</td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            onClick={() => openHold(it)}
                            title="Place on Hold"
                            aria-label={`Place ${it.part_number} on Hold`}
                            disabled={stock === 0}
                            variant="info"
                            size="row-icon"
                            data-testid={`inventory-hold-btn-${it.part_number}`}
                          >
                            <PauseCircle />
                          </Button>
                          {!usesMoreActions ? (
                            <Button
                              type="button"
                              onClick={() => setQrItem(it)}
                              title="View QR"
                              aria-label={`View QR for ${it.part_number}`}
                              variant="info"
                              size="row-icon"
                              data-testid={`inventory-qr-btn-${it.part_number}`}
                            >
                              <QrCode />
                            </Button>
                          ) : null}
                          {usesMoreActions ? <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                title="More actions"
                                aria-label={`More actions for ${it.part_number}`}
                                variant="neutral"
                                size="row-icon"
                                data-testid={`inventory-more-btn-${it.part_number}`}
                              >
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem
                                onSelect={() => setQrItem(it)}
                                data-testid={`inventory-qr-btn-${it.part_number}`}
                              >
                                <QrCode />
                                QR
                              </DropdownMenuItem>
                              {isAdmin && (
                                <>
                                  <DropdownMenuItem
                                    onSelect={() => openEdit(it)}
                                    data-testid={`inventory-edit-btn-${it.part_number}`}
                                  >
                                    <Pencil />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => remove(it)}
                                    data-testid={`inventory-delete-btn-${it.part_number}`}
                                    variant="destructive"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ListTable>
        </ListTableViewport>
        {/* Pagination */}
        <ListPagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          totalPages={totalPages}
          loading={loading}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setPage(1);
          }}
          pageSizeAriaLabel="Inventory items per page"
          testId="inventory-pagination"
          summaryTestId="inventory-count"
          pageTestId="inventory-page-summary"
          previousTestId="pagination-prev"
          nextTestId="pagination-next"
        />
      </ListTableShell>

      {/* Edit modal */}
      {editOpen && editing && (
        <FlowpiDialog
          open={editOpen}
          onClose={closeItemEditor}
          busy={itemSaving}
          size="form"
          eyebrow="Inventory"
          title={editing.id ? "Edit Item" : "New Item"}
          testId="inventory-modal"
          footer={(
            <>
              <Button type="button" onClick={closeItemEditor} disabled={itemSaving} variant="cancel" data-testid="item-cancel-btn">Cancel</Button>
              <Button type="button" onClick={save} loading={itemSaving} loadingLabel="Saving..." variant="success" data-testid="item-save-btn">Save</Button>
            </>
          )}
        >
          <div className="grid grid-cols-2 gap-4">
            <Field label="Part number" required>
              <Input className="form-control" readOnly={!!editing.id} locked={!!editing.id} value={editing.part_number} onChange={(e) => setEditing({ ...editing, part_number: e.target.value })} data-testid="item-part-number-input" />
            </Field>
            <Field label="Model">
              <Input className="form-control" value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} data-testid="item-model-input" />
            </Field>
            <Field label="Compatible Models" className="col-span-2">
              <div
                className="form-control-container flex cursor-text flex-wrap items-center gap-1.5 px-2 py-1.5"
                onClick={(e) => e.currentTarget.querySelector("input")?.focus()}
              >
                {(editing.compatible_models || []).map((tag, i) => (
                  <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-[2px] text-xs font-medium text-zinc-700">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, compatible_models: editing.compatible_models.filter((_, idx) => idx !== i) })}
                      className="rounded-[var(--action-radius-icon)] text-zinc-400 leading-none hover:bg-accent hover:text-zinc-700"
                      aria-label={`Remove compatible model ${tag}`}
                    >×</button>
                  </span>
                ))}
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const val = tagInput.trim().replace(/,$/, "");
                      if (val && !(editing.compatible_models || []).includes(val)) {
                        setEditing({ ...editing, compatible_models: [...(editing.compatible_models || []), val] });
                      }
                      setTagInput("");
                    } else if (e.key === "Backspace" && !tagInput && (editing.compatible_models || []).length > 0) {
                      setEditing({ ...editing, compatible_models: editing.compatible_models.slice(0, -1) });
                    }
                  }}
                  placeholder={(editing.compatible_models || []).length === 0 ? "Type model and press Enter..." : ""}
                  className="flex-1 min-w-[160px] outline-none text-[13px] bg-transparent py-0.5"
                  data-testid="item-compatible-models-input"
                />
              </div>
            </Field>
            <Field label="Part name" required>
              <Input className="form-control" value={editing.part_name} onChange={(e) => setEditing({ ...editing, part_name: e.target.value })} data-testid="item-name-input" />
            </Field>
            <Field label="Rack location">
              <Input className="form-control" value={editing.rack_location} onChange={(e) => setEditing({ ...editing, rack_location: e.target.value })} data-testid="item-rack-input" />
            </Field>
            <Field label="Price (IDR)">
              <Input className="form-control" type="number" step="1" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="item-price-input" />
            </Field>
            <Field label="Low-stock threshold">
              <Input className="form-control" type="number" value={editing.low_stock_threshold} onChange={(e) => setEditing({ ...editing, low_stock_threshold: e.target.value })} data-testid="item-threshold-input" />
            </Field>
            <Field label="Description" className="col-span-2">
              <Textarea className="form-control form-control--textarea resize-y" rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="item-description-input" />
            </Field>

            <div className="col-span-2">
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">
                {editing.id ? "Stock per warehouse" : "Initial stock per warehouse"}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {warehouses.map((w) => (
                  <div key={w.id}>
                    <div className="text-[11px] text-zinc-500 mb-1">{w.name}</div>
                    <Input
                      className="form-control"
                      type="number"
                      min="0"
                      value={editing.stock?.[w.id] ?? 0}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          stock: { ...editing.stock, [w.id]: Number(e.target.value) || 0 },
                        })
                      }
                      data-testid={`item-stock-${w.code}-input`}
                    />
                  </div>
                ))}
              </div>
              {editing.id && (
                <div className="text-[10px] text-zinc-500 mt-2 italic">
                  Stock changes will be applied immediately to the selected warehouses.
                </div>
              )}
            </div>
          </div>
          <FormError className="mt-5" data-testid="inventory-item-save-error">{itemSaveError}</FormError>
        </FlowpiDialog>
      )}
      {itemGuard.dialog}

      {/* QR modal */}
      {qrItem && (
        <Modal title={`Barcode · ${qrItem.part_number}`} onClose={() => setQrItem(null)} testid="qr-modal" size="sm">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white p-4 border border-zinc-200">
              <QRCodeSVG value={qrItem.part_number} size={192} level="M" />
            </div>
            <div className="text-center">
              <div className="text-sm font-semibold">{qrItem.part_number}</div>
              <div className="text-xs text-zinc-500">{qrItem.part_name}</div>
            </div>
          </div>
        </Modal>
      )}

      {/* Import dialog */}
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} warehouses={warehouses} onImported={load} />

      {/* Bulk Edit dialog */}
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} onUpdated={load} />

      {/* Compatible Models dialog */}
      <CompatibleModelsDialog open={compatModelsOpen} onClose={() => setCompatModelsOpen(false)} onUpdated={load} />

      {/* Inline Hold modal */}
      <HoldModal
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        item={holdItem}
        warehouse={activeWhObj}
        onCreated={load}
      />
      <FlowpiAlertDialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError("");
        }}
        busy={deleteSubmitting}
        title="Delete Inventory Item?"
        description="This consequential action requires confirmation."
        footer={(
          <>
            <Button variant="cancel" disabled={deleteSubmitting} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" loading={deleteSubmitting} loadingLabel="Deleting..." onClick={confirmRemove}>
              Delete Item
            </Button>
          </>
        )}
        testId="inventory-delete-dialog"
      >
        <p>Delete <strong>{deleteTarget?.part_number}</strong>?</p>
        <FormError className="mt-4">{deleteError}</FormError>
      </FlowpiAlertDialog>
    </OrdinaryPageBody>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <FormField className={className}>
      <FieldLabel required={required}>{label}</FieldLabel>
      {children}
    </FormField>
  );
}

// Re-export Modal for existing Hold page import
export { Modal };

// inject input styles once
const _s = document.createElement("style");
_s.innerHTML = `.wms-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));color:hsl(var(--foreground));padding:10px 12px;font-size:13px;border-radius:calc(var(--radius) - 0.25rem);outline:none}
.wms-input:focus{border-color:hsl(var(--ring));box-shadow:0 0 0 3px hsl(var(--ring) / 0.14)}
.wms-filter-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));color:hsl(var(--foreground));padding:6px 9px;font-size:11px;border-radius:calc(var(--radius) - 0.25rem);outline:none;font-family:inherit;font-weight:400}
.wms-filter-input:focus{border-color:hsl(var(--ring));box-shadow:0 0 0 3px hsl(var(--ring) / 0.14)}`;
if (typeof document !== "undefined" && !document.getElementById("wms-input-style")) {
  _s.id = "wms-input-style";
  document.head.appendChild(_s);
}
